/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import type { ComponentProps, ReactNode } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import type {
  BackgroundColorProps,
  BorderProps,
  DisplayProps,
  FlexProps,
  HeightProps,
  JustifyContentProps,
  MinHeightProps,
  WidthProps,
} from 'styled-system';
import {
  backgroundColor,
  border,
  display,
  flex,
  height,
  justifyContent,
  minHeight,
  space,
  themeGet,
  width,
} from 'styled-system';

import { useLocalization } from 'common/providers/LocaleProvider';
import { focusOutlineCSS } from 'common/theme/styles';
import type { SpaceProps } from 'common/theme/system-types';
import type { CursorProps, FlexShrinkProps } from 'common/theme/system-utilities';
import { cursor, flexShrink } from 'common/theme/system-utilities';
import type { GalleryTrackingProps } from 'common/tracking/productGallery';
import { ArrowDirection, dispatchArrowButtonClick } from 'common/tracking/productGallery';
import type { ResponsiveImageWidths } from 'common/types/Image';
import { Flex } from '../Flex';
import type { SetVideoPlayer } from '../YouTubeVideo/hooks';
import type { FallbackImageStyles } from './CarouselFallbackImage';
import CarouselFallbackImage from './CarouselFallbackImage';
import { CarouselPagination } from './CarouselPagination';
import CarouselSlideContent from './CarouselSlideContent';
import CarouselTrackWrapper from './CarouselTrack';
import { useCarousel } from './hooks/use-carousel';
import { useProgressiveImages } from './hooks/use-progressive-images';
import { useResponsiveSlides } from './hooks/use-responsive-slide';
import type { ImageCarouselNavigationProps } from './ImageCarouselNavigation';
import { ImageCarouselNavigation } from './ImageCarouselNavigation';
import { type ArticleMedia, type CarouselSlideProps } from './types';
import type { ImageCarouselRenderPaginationFn, PaginationProps } from './utils';
import { getSlideHref, isVideo } from './utils';

const CAROUSEL_DEFAULT_GAP = 4;

interface ImageCarouselProps {
  items: ArticleMedia[];
  name: string; // Carousel name (e.g., "Product Gallery")
  withContainerAriaLabel?: boolean;
  widths: ResponsiveImageWidths;
  handleClick?: () => void;
  handlePinch?: (event: React.TouchEvent) => void;
  handleKeyDown?: (event: KeyboardEvent | React.KeyboardEvent<unknown>) => void;
  className?: string;
  activeSlide?: number;
  href?: string;
  showNavigation?: boolean;
  showPagination?: boolean;
  slidesToShow?: number;
  wrapAround?: boolean;
  renderExtra?: () => ReactNode;
  renderSlide?: (carouselSlideProps: CarouselSlideProps) => React.ReactNode;
  renderPagination?: ImageCarouselRenderPaginationFn<PaginationProps>;
  fallbackSlideWidth?: number | string;
  setVideoPlayer?: SetVideoPlayer;
  onAfterSlide?: (activeSlide: number) => void;
  onNext?: (fromIndex: number) => void;
  onPrevious?: (fromIndex: number) => void;
  renderControls?: (props: ImageCarouselNavigationProps) => React.ReactNode;
  arrowIconSize?: number;
  fallbackImageStyles?: FallbackImageStyles;
  paginationStyles?: ComponentProps<typeof CarouselPagination>['paginationStyles'];
  slideAriaLabel?: string; // Aria label for slides
  baseId: string;
  isLazy?: boolean; // Lazy load first image (only affects first image, rest are always lazy)
  highPriority?: boolean; // High priority for first image (only affects first image)
  progressiveLoading?: boolean; // Load images on demand
  productGalleryV2?: boolean;
  trackingProps?: GalleryTrackingProps;
}

const carouselViewportStyles: ComponentProps<typeof Flex> = {
  width: 1,
  position: 'relative',
  height: 'auto',
  alignItems: 'center',
  overflow: 'hidden',
};

const carouselContainerStyles: ComponentProps<typeof Flex> = {
  width: 1,
  position: 'relative',
  height: 'auto',
  alignItems: 'center',
};

const CarouselListItem = styled.div<
  WidthProps &
    HeightProps &
    FlexProps &
    FlexShrinkProps &
    JustifyContentProps &
    BorderProps &
    BackgroundColorProps &
    MinHeightProps
>`
  ${width};
  ${height};
  ${flex};
  ${justifyContent};
  ${flexShrink};
  ${minHeight};
`;

const carouselListItemStyles: ComponentProps<typeof CarouselListItem> = {
  flexShrink: 0,
  width: '100%',
  justifyContent: 'center',
  border: 'none',
  backgroundColor: 'transparent',
};

const CarouselLink = styled.a<WidthProps & HeightProps>`
  ${display};
  ${width};
  ${height};

  &:focus-visible {
    outline: none;
    img {
      border: 2px solid ${themeGet('colors.sys.secondary.border.focus')};
    }
  }
`;

const CarouselButton = styled.button<
  WidthProps &
    HeightProps &
    BackgroundColorProps &
    DisplayProps &
    BorderProps &
    CursorProps &
    SpaceProps
>`
  ${backgroundColor};
  ${border};
  ${cursor};
  ${display};
  ${focusOutlineCSS};
  ${height};
  ${width};
  ${space};

  &:focus-visible {
    outline: none;
    img {
      border: 2px solid ${themeGet('colors.sys.secondary.border.focus')};
    }
  }
`;

const carouselButtonStyles: ComponentProps<typeof CarouselButton> = {
  display: 'block',
  width: '100%',
  height: '100%',
  backgroundColor: 'transparent',
  border: 'none',
  cursor: 'zoom-in',
};

const CarouselDiv = styled.div<WidthProps & HeightProps & SpaceProps>`
  ${display};
  ${width};
  ${height};
`;

const carouselStyles: WidthProps & HeightProps & DisplayProps & SpaceProps = {
  display: 'block',
  width: '100%',
  height: '100%',
};

interface CarouselSlidesProps {
  cloneImages: ArticleMedia[];
  currentIndex: number;
  slideWidth: number;
  fallbackSlideWidth: number | string;
  widths: ResponsiveImageWidths;
  setVideoPlayer?: SetVideoPlayer;
  renderSlide?: (carouselSlideProps: CarouselSlideProps) => React.ReactNode;
  href?: string;
  handleClick?: (event: React.MouseEvent) => void;
  handlePinch?: (event: React.TouchEvent) => void;
  handleKeyDown?: (event: KeyboardEvent | React.KeyboardEvent<unknown>) => void;
  baseId: string;
  slideAriaLabel?: string;
  isLazy?: boolean;
  highPriority?: boolean;
  progressiveLoading?: boolean;
  onNext?: (fromIndex: number) => void;
  onPrevious?: (fromIndex: number) => void;
  hasInteracted?: boolean;
  name: string;
  productGalleryV2?: boolean;
}

const CarouselSlides = ({
  cloneImages,
  currentIndex,
  slideWidth,
  fallbackSlideWidth,
  widths,
  setVideoPlayer,
  renderSlide,
  href,
  handleClick,
  handlePinch,
  handleKeyDown,
  baseId,
  slideAriaLabel,
  isLazy,
  highPriority,
  name,
  progressiveLoading = false,
  hasInteracted = false,
  productGalleryV2 = false,
}: CarouselSlidesProps) => {
  const { t } = useLocalization();

  const visibleSlides = useProgressiveImages(cloneImages, progressiveLoading, hasInteracted);

  return (
    <>
      {visibleSlides.map((image, index) => {
        const slideContentProps = {
          cloneImage: image,
          currentIndex,
          itemIndex: index,
          widths,
          setVideoPlayer,
          renderSlide,
          name,
          isLazy: isLazy || index >= 1, // Always lazy load after 2nd image
          highPriority: highPriority && index === 0, // Only affects first image
        };
        let slideContent;

        const isActive = currentIndex === index;
        const tabIndex = isActive ? 0 : -1;
        if (href) {
          slideContent = (
            <CarouselLink
              {...carouselStyles}
              {...(handleClick && { onClick: handleClick })}
              aria-label={slideAriaLabel}
              href={getSlideHref({ index, href })}
              onKeyDown={handleKeyDown}
              onTouchStart={handlePinch}
              tabIndex={tabIndex}
            >
              <CarouselSlideContent {...slideContentProps} />
            </CarouselLink>
          );
        } else if (handleClick) {
          slideContent = (
            <CarouselButton
              {...carouselButtonStyles}
              p={productGalleryV2 ? 'sp_0' : undefined}
              aria-label={slideAriaLabel}
              onClick={e => !isVideo(image) && handleClick?.(e)}
              onKeyDown={handleKeyDown}
              onTouchStart={handlePinch}
              tabIndex={tabIndex}
            >
              <CarouselSlideContent {...slideContentProps} />
            </CarouselButton>
          );
        } else {
          slideContent = (
            <CarouselDiv {...carouselStyles}>
              <CarouselSlideContent {...slideContentProps} />
            </CarouselDiv>
          );
        }

        const slideId = `${baseId}-slide-${index}`;

        return (
          <CarouselListItem
            {...carouselListItemStyles}
            key={slideId}
            id={slideId}
            aria-current={currentIndex === index}
            aria-hidden={currentIndex !== index}
            aria-roledescription={t('h24_carousel_slide')}
            aria-label={`${currentIndex + 1} ${t('h24_of')} ${cloneImages.length}`}
            data-testid="carousel-slide"
            minHeight={isVideo(image) ? slideWidth : 'auto'}
            role="tabpanel"
            width={slideWidth || fallbackSlideWidth}
          >
            {slideContent}
          </CarouselListItem>
        );
      })}
    </>
  );
};

// eslint-disable-next-line complexity
const ImageCarousel = ({
  activeSlide,
  items,
  name,
  widths,
  handleClick,
  handlePinch,
  handleKeyDown,
  className = '',
  showNavigation = true,
  showPagination = true,
  slidesToShow = 1,
  wrapAround = false,
  href,
  fallbackSlideWidth = 1,
  fallbackImageStyles,
  paginationStyles,
  arrowIconSize,
  renderExtra,
  renderSlide,
  setVideoPlayer,
  onAfterSlide,
  onNext,
  onPrevious,
  renderControls = props => <ImageCarouselNavigation {...props} />,
  slideAriaLabel,
  baseId,
  isLazy,
  highPriority,
  withContainerAriaLabel = true,
  progressiveLoading = false,
  productGalleryV2 = false,
  trackingProps,
}: ImageCarouselProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isInitialMount = useRef(true);
  const previousIndexRef = useRef<number | undefined>(undefined);
  const [hasInteracted, setHasInteracted] = useState(false);
  const previousSlideWidth = useRef<number>(0);

  const {
    currentIndex,
    slideCount,
    goToSlide,
    nextSlide,
    previousSlide,
    cloneImages,
    paginationIndex,
  } = useCarousel({
    items,
    slidesToShow,
    wrapAround,
    initialSlide: activeSlide ?? 0,
  });

  const slideWidth = useResponsiveSlides({ containerRef, slidesToShow, gap: CAROUSEL_DEFAULT_GAP });

  // Disable transitions if slideWidth is changing (during initial sizing) or on initial mount
  const slideWidthHasChanged = slideWidth !== previousSlideWidth.current;
  const shouldDisableTransition = isInitialMount.current || slideWidthHasChanged;

  // Update previous slideWidth for next render
  previousSlideWidth.current = slideWidth;

  const hasPrev = !wrapAround && currentIndex === 0;
  const hasNext = !wrapAround && currentIndex === slideCount - 1;

  const handleOnNext = () => {
    const fromIndex = currentIndex;
    const toIndex = currentIndex + 1;
    if (toIndex < slideCount) {
      setHasInteracted(true);
      onNext?.(fromIndex);
    }
  };

  const handleOnPrevious = () => {
    const fromIndex = currentIndex;
    const toIndex = currentIndex - 1;
    if (toIndex >= 0) {
      setHasInteracted(true);
      onPrevious?.(fromIndex);
    }
  };

  const handlePaginationClick = (index: number) => {
    if (index > currentIndex) {
      onNext?.(currentIndex);
    } else if (index < currentIndex) {
      onPrevious?.(currentIndex);
    }
    setHasInteracted(true);
    goToSlide(index);
  };

  const handlePaginationNext = () => {
    nextSlide();
    handleOnNext();
  };

  const handlePaginationPrevious = () => {
    previousSlide();
    handleOnPrevious();
  };

  useEffect(() => {
    if (isInitialMount.current) return; // Don't run on initial mount since we initialize correctly
    if (activeSlide === null || activeSlide === undefined || currentIndex === activeSlide) return;
    goToSlide(activeSlide);
  }, [activeSlide]);

  useEffect(() => {
    if (!isInitialMount.current && previousIndexRef.current !== currentIndex) {
      onAfterSlide?.(currentIndex);
    }

    previousIndexRef.current = currentIndex;

    if (isInitialMount.current) {
      isInitialMount.current = false;
    }
  }, [currentIndex, onAfterSlide]);

  if (!items.length) {
    return (
      <CarouselFallbackImage
        name={name}
        href={href}
        widths={widths}
        fallbackImageStyles={fallbackImageStyles}
        handleClick={handleClick}
      />
    );
  }

  const handleContainerKeyDown = productGalleryV2
    ? (e: React.KeyboardEvent) => {
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          nextSlide();
          handleOnNext();
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          previousSlide();
          handleOnPrevious();
        }
      }
    : undefined;

  return (
    <Flex
      {...carouselContainerStyles}
      as="section"
      {...(withContainerAriaLabel ? { 'aria-label': name } : {})}
      aria-roledescription="carousel"
      data-testid="image-carousel-container"
      onKeyDown={handleContainerKeyDown}
    >
      {renderExtra?.()}
      {showNavigation &&
        slideCount > slidesToShow &&
        renderControls({
          hasPrev,
          hasNext,
          onNext: () => {
            nextSlide();
            handleOnNext();
            dispatchArrowButtonClick(ArrowDirection.Next, trackingProps);
          },
          onPrevious: () => {
            previousSlide();
            handleOnPrevious();
            dispatchArrowButtonClick(ArrowDirection.Previous, trackingProps);
          },
          arrowIconSize,
        })}
      {showPagination && slideCount > slidesToShow && (
        <CarouselPagination
          baseId={baseId}
          currentSlide={paginationIndex}
          goToSlide={handlePaginationClick}
          nextSlide={handlePaginationNext}
          paginationStyles={paginationStyles}
          previousSlide={handlePaginationPrevious}
          slideCount={slideCount}
        />
      )}
      <Flex
        {...carouselViewportStyles}
        ref={containerRef}
        className={className}
        data-testid="carousel-container"
      >
        <CarouselTrackWrapper
          currentIndex={currentIndex}
          gap={CAROUSEL_DEFAULT_GAP}
          nextSlide={() => {
            nextSlide();
            handleOnNext();
          }}
          previousSlide={() => {
            previousSlide();
            handleOnPrevious();
          }}
          slideWidth={slideWidth}
          disableTransition={shouldDisableTransition}
        >
          <CarouselSlides
            baseId={baseId}
            cloneImages={cloneImages}
            currentIndex={currentIndex}
            fallbackSlideWidth={fallbackSlideWidth}
            handleClick={handleClick}
            handleKeyDown={handleKeyDown}
            handlePinch={handlePinch}
            href={href}
            renderSlide={renderSlide}
            setVideoPlayer={setVideoPlayer}
            slideAriaLabel={slideAriaLabel}
            slideWidth={slideWidth}
            widths={widths}
            isLazy={isLazy}
            highPriority={highPriority}
            progressiveLoading={progressiveLoading}
            hasInteracted={hasInteracted}
            name={name}
            productGalleryV2={productGalleryV2}
          />
        </CarouselTrackWrapper>
      </Flex>
    </Flex>
  );
};

export default ImageCarousel;
